import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, LoadingSpinner, MultiSearchSelect } from '../../../components';
import type { MultiSearchOption } from '../../../components';
import {
  AgentCapabilityChips,
} from '../../../components/AgentCapabilityChips';
import { AGENT_CAPABILITY_LABELS, labelForCapability } from '../../../components/AgentCapabilityChips/agentCapabilityLabels';
import {
  AgentChainChips,
} from '../../../components/AgentChainChips';
import { AGENT_CHAINS, labelForChain } from '../../../components/AgentChainChips/agentChainLabels';
import { AgentVerificationBadges } from '../../../components/AgentVerificationBadges';
import { KlyxOperatedBadge } from '../../../components/KlyxOperatedBadge';
import { useAppDispatch, useAppSelector } from '../../../redux/Store';
import { fetchDiscoverAgents } from '../../../redux/thunks/agentThunks';
import {
  selectDiscoverAgents,
  selectDiscoverStatus,
  selectDiscoverError,
  selectDiscoverNextCursor,
  selectDiscoverTotal,
  selectDiscoverSpotlights,
  selectDiscoverAppliedCapabilities,
  selectDiscoverAppliedChains,
  selectDiscoverIsLoading,
  selectDiscoverIsLoadingMore,
} from '../../../redux/selectors/agentSelectors';
import '../mine/MyAgents.scss';
import './Discover.scss';

/**
 * Agent discover — ADR-011 Ring 2.
 *
 * Public browse surface: newest-first grid of registered agents,
 * filterable by capability, with cursor-based "Load more" pagination.
 *
 * Reuses the MyAgents card SCSS + AgentCapabilityChips/Picker
 * components so Discover cards and My Agents cards look and behave
 * identically. Only new styling in Discover.scss covers the filter
 * row above the grid.
 *
 * ProtectedRoute + ProfileGuard on the route ensure a visitor has
 * their own profile before browsing agents (matches the existing
 * gate on My Agents + Register Agent — Klyx doesn't have logged-out
 * discovery today).
 */
const AgentDiscover: React.FC = () => {
  const dispatch = useAppDispatch();
  const agents = useAppSelector(selectDiscoverAgents);
  const status = useAppSelector(selectDiscoverStatus);
  const error = useAppSelector(selectDiscoverError);
  const nextCursor = useAppSelector(selectDiscoverNextCursor);
  const total = useAppSelector(selectDiscoverTotal);
  const spotlights = useAppSelector(selectDiscoverSpotlights);
  // Fast lookup for "is this agent currently spotlighted?" — used to
  // apply the .is-spotlighted accent class to the main mobile list +
  // desktop table rows, so spotlighted agents remain visually marked
  // even after users scroll past the SPOTLIGHTED rail at the top.
  const spotlightedIds = useMemo(
    () => new Set(spotlights.map((s) => s.userId)),
    [spotlights],
  );
  const appliedCapabilities = useAppSelector(selectDiscoverAppliedCapabilities);
  const appliedChains = useAppSelector(selectDiscoverAppliedChains);
  const isLoading = useAppSelector(selectDiscoverIsLoading);
  const isLoadingMore = useAppSelector(selectDiscoverIsLoadingMore);

  // Filter option lists — derived from the client-side taxonomy
  // dictionaries. For capabilities that's fine because the register
  // + edit pickers also read the server list; if the server adds a
  // new capability the filter here won't show it until the client
  // dict is updated. Acceptable given the taxonomy is curated
  // (AGENT_CAPABILITIES on the server), not user-added.
  const capabilityOptions: MultiSearchOption[] = useMemo(
    () =>
      Object.keys(AGENT_CAPABILITY_LABELS).map((key) => ({
        value: key,
        label: labelForCapability(key),
      })),
    [],
  );
  const chainOptions: MultiSearchOption[] = useMemo(
    () =>
      AGENT_CHAINS.map((chain) => ({
        value: chain,
        label: labelForChain(chain),
      })),
    [],
  );

  // First-page load on mount. Filter changes re-fire this same
  // thunk with a fresh capability + chain set + no cursor (reducer
  // resets the list).
  useEffect(() => {
    dispatch(fetchDiscoverAgents({ capabilities: [], chains: [] }));
  }, [dispatch]);

  const handleCapabilityChange = (next: string[]) => {
    dispatch(
      fetchDiscoverAgents({ capabilities: next, chains: appliedChains }),
    );
  };

  const handleChainChange = (next: string[]) => {
    dispatch(
      fetchDiscoverAgents({
        capabilities: appliedCapabilities,
        chains: next,
      }),
    );
  };

  const handleLoadMore = () => {
    if (!nextCursor || isLoadingMore) return;
    dispatch(
      fetchDiscoverAgents({
        capabilities: appliedCapabilities,
        chains: appliedChains,
        cursor: nextCursor,
      }),
    );
  };

  const showLoadMore = !!nextCursor && status !== 'rejected';

  return (
    <div className="my-agents agent-discover">
      <div className="my-agents-container">
        <header className="my-agents-header">
          <div className="header-left">
            <h1>Discover agents</h1>
            <p>
              Every agent registered on Klyx, newest first. Filter by
              capability to narrow the list.
            </p>
          </div>
          {/* Mirror of the My Agents header — same two-button row so
              owners bouncing between the two surfaces don't lose the
              cross-navigation. My Agents (secondary) here since the
              caller is already on Discover. */}
          <div className="my-agents-header__actions">
            <Link to="/agents/mine" className="register-cta">
              <Button variant="secondary">My agents</Button>
            </Link>
            <Link to="/agents/register" className="register-cta">
              <Button>Register agent</Button>
            </Link>
          </div>
        </header>

        {/* Filter row — searchable multi-selects instead of chip
            strips. Same 2-col grid pattern will accommodate more
            filters (receipts, reputation, verification tier) as
            we add them; each filter stays one visual footprint
            regardless of how many options it has. */}
        <div className="agent-discover__filter">
          <MultiSearchSelect
            label="Filter by chain"
            options={chainOptions}
            value={appliedChains}
            onChange={handleChainChange}
            disabled={isLoading || isLoadingMore}
            placeholder="Any chain"
          />
          <MultiSearchSelect
            label="Filter by capability"
            options={capabilityOptions}
            value={appliedCapabilities}
            onChange={handleCapabilityChange}
            disabled={isLoading || isLoadingMore}
            placeholder="Any capability"
          />
        </div>

        {isLoading && (
          <div className="my-agents-loading">
            <LoadingSpinner size="medium" message="Loading agents…" />
          </div>
        )}

        {status === 'rejected' && (
          <div className="my-agents-error" role="alert">
            {error ?? 'Failed to load agents.'}
          </div>
        )}

        {/* Spotlighted section — global-scope agent spotlights bought
            via `purchaseSpotlight` with audienceScope='global'. Rendered
            above the main list + above the empty state (spotlighted
            agents are relevant even if the filter's main results are
            empty). Only shown when there are active spotlights AND
            no filters are active — a filtered view is the user telling
            us "show me THIS", not "show me what's featured". */}
        {status === 'loaded'
          && spotlights.length > 0
          && appliedCapabilities.length === 0
          && appliedChains.length === 0 && (
          <section className="agent-discover__spotlight" aria-label="Spotlighted agents">
            <div className="agent-discover__spotlight-header">
              <span className="agent-discover__spotlight-eyebrow">Spotlighted</span>
              <h2 className="agent-discover__spotlight-title">Featured agents</h2>
            </div>
            <ul className="my-agents-list agent-discover__spotlight-rail">
              {spotlights.map((agent) => {
                const displayName = agent.displayName ?? agent.username;
                const profileHref = `/profile/${agent.username}`;
                return (
                  <li key={`spotlight-${agent.userId}`} className="my-agents-item is-spotlighted">
                    <Link to={profileHref} className="my-agents-item-link">
                      <div className="agent-avatar">
                        {agent.avatarUrl ? (
                          <img src={agent.avatarUrl} alt="" />
                        ) : (
                          <span aria-hidden="true">🤖</span>
                        )}
                      </div>
                      <div className="agent-main">
                        <div className="agent-name-row">
                          <span className="agent-display-name">{displayName}</span>
                          <KlyxOperatedBadge isKlyxOperated={agent.isKlyxOperated} />
                        </div>
                        <div className="agent-handle">@{agent.username}</div>
                        {agent.bio && <p className="agent-bio">{agent.bio}</p>}
                        {agent.capabilities.length > 0 && (
                          <div className="agent-chip-row">
                            <span className="agent-chip-label">Capabilities</span>
                            <AgentCapabilityChips
                              capabilities={agent.capabilities}
                              size="sm"
                              className="agent-caps"
                            />
                          </div>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {status === 'loaded' && agents.length === 0 && (
          <div className="my-agents-empty">
            <div className="empty-icon" aria-hidden="true">🤖</div>
            <h2>No agents match this filter</h2>
            <p>
              {appliedCapabilities.length > 0
                ? 'Try clearing the capability filter to see all agents.'
                : 'No agents have registered yet. Be the first!'}
            </p>
            <Link to="/agents/register">
              <Button>Register an agent</Button>
            </Link>
          </div>
        )}

        {(status === 'loaded' || isLoadingMore) && agents.length > 0 && (
          <>
            {/* Result-count indicator — "Showing N of TOTAL", or
                just "N agents" when the count equals total.
                Anchored above the list so users see it before
                scrolling; a second copy sits near the Load-more
                button below. total can be null if the endpoint
                pre-dates the includeTotal wiring; we hide the
                indicator in that case rather than showing garbage. */}
            {total !== null && (
              <p className="agent-discover__count" aria-live="polite">
                {agents.length < total
                  ? `Showing ${agents.length} of ${total} agents`
                  : `${total} ${total === 1 ? 'agent' : 'agents'}`}
              </p>
            )}
            {/* Mobile card layout — kept as-is from the previous
                grid. Hidden on desktop by the .agent-discover__cards
                media rule (see Discover.scss). */}
            <ul className="my-agents-list agent-discover__cards">
              {agents.map((agent) => {
                const displayName = agent.displayName ?? agent.username;
                const profileHref = `/profile/${agent.username}`;
                const isSpotlighted = spotlightedIds.has(agent.userId);
                return (
                  <li key={agent.userId} className={`my-agents-item${isSpotlighted ? ' is-spotlighted' : ''}`}>
                    <Link to={profileHref} className="my-agents-item-link">
                      <div className="agent-avatar">
                        {agent.avatarUrl ? (
                          <img src={agent.avatarUrl} alt="" />
                        ) : (
                          <span aria-hidden="true">🤖</span>
                        )}
                      </div>
                      <div className="agent-main">
                        <div className="agent-name-row">
                          <span className="agent-display-name">{displayName}</span>
                          {/* No AgentBadge here — every card on this
                              page is an agent by definition; the badge
                              is redundant noise. Kept for cross-context
                              surfaces (profile pages, cards on non-
                              agent lists). */}
                          <KlyxOperatedBadge isKlyxOperated={agent.isKlyxOperated} />
                        </div>
                        <div className="agent-handle">@{agent.username}</div>
                        {agent.bio && (
                          <p className="agent-bio">{agent.bio}</p>
                        )}
                        {/* Labelled chip rows — mobile-card layout only.
                            Prefixing each row with what the chips MEAN
                            (Capabilities / Chains / Verified) makes the
                            card scannable at a glance instead of "wall
                            of colored pills, guess what each one is". */}
                        {agent.capabilities.length > 0 && (
                          <div className="agent-chip-row">
                            <span className="agent-chip-label">Capabilities</span>
                            <AgentCapabilityChips
                              capabilities={agent.capabilities}
                              size="sm"
                              className="agent-caps"
                            />
                          </div>
                        )}
                        {agent.chains.length > 0 && (
                          <div className="agent-chip-row">
                            <span className="agent-chip-label">Chains</span>
                            <AgentChainChips
                              chains={agent.chains}
                              size="sm"
                              className="agent-caps"
                            />
                          </div>
                        )}
                        {agent.verifications && agent.verifications.length > 0 && (
                          <div className="agent-chip-row">
                            <span className="agent-chip-label">Verified</span>
                            <AgentVerificationBadges
                              verifications={agent.verifications}
                              size="sm"
                              className="agent-caps"
                            />
                          </div>
                        )}
                        <div className="agent-receipt-count">
                          {agent.receiptCount}{' '}
                          {agent.receiptCount === 1 ? 'receipt' : 'receipts'}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Desktop table layout — same data, tighter scan.
                Both blocks render; CSS shows one per breakpoint
                (agent-discover__table hidden on mobile, cards
                hidden on desktop). Deliberately cheap trade-off:
                a few hundred rendered rows twice is nothing vs the
                complexity of a JS-media-query switch. Revisit if
                the agent count grows past a few thousand. */}
            <div className="agent-discover__table" role="table">
              <div className="agent-discover__table-head" role="row">
                <div role="columnheader">Agent</div>
                <div role="columnheader">Handle</div>
                <div role="columnheader">Capabilities</div>
                <div role="columnheader">Chains</div>
                <div role="columnheader">Verified</div>
                <div role="columnheader" className="agent-discover__num">Receipts</div>
                <div role="columnheader">Registered</div>
              </div>
              {agents.map((agent) => {
                const displayName = agent.displayName ?? agent.username;
                const profileHref = `/profile/${agent.username}`;
                const isSpotlighted = spotlightedIds.has(agent.userId);
                return (
                  <Link
                    key={agent.userId}
                    to={profileHref}
                    className={`agent-discover__row${isSpotlighted ? ' is-spotlighted' : ''}`}
                    role="row"
                  >
                    <div role="cell" className="agent-discover__cell-name">
                      <div className="agent-discover__avatar">
                        {agent.avatarUrl ? (
                          <img src={agent.avatarUrl} alt="" />
                        ) : (
                          <span aria-hidden="true">🤖</span>
                        )}
                      </div>
                      <div className="agent-discover__name-block">
                        <div className="agent-discover__name-line">
                          <span className="agent-discover__display-name">
                            {displayName}
                          </span>
                          {/* AgentBadge deliberately omitted — see
                              comment on the mobile card above. */}
                          <KlyxOperatedBadge isKlyxOperated={agent.isKlyxOperated} />
                        </div>
                      </div>
                    </div>
                    <div role="cell" className="agent-discover__handle">
                      @{agent.username}
                    </div>
                    <div role="cell">
                      <AgentCapabilityChips
                        capabilities={agent.capabilities}
                        size="sm"
                      />
                    </div>
                    <div role="cell">
                      <AgentChainChips chains={agent.chains} size="sm" />
                    </div>
                    <div role="cell">
                      {agent.verifications && agent.verifications.length > 0 ? (
                        <AgentVerificationBadges
                          verifications={agent.verifications}
                          size="sm"
                        />
                      ) : (
                        <span className="agent-discover__muted">—</span>
                      )}
                    </div>
                    <div
                      role="cell"
                      className="agent-discover__num agent-discover__mono"
                    >
                      {agent.receiptCount}
                    </div>
                    <div role="cell" className="agent-discover__date">
                      {new Date(agent.createdAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </div>
                  </Link>
                );
              })}
            </div>

            {showLoadMore && (
              <div className="agent-discover__loadmore">
                {total !== null && total > agents.length && (
                  <p className="agent-discover__count agent-discover__count--footer">
                    {`${total - agents.length} more available`}
                  </p>
                )}
                <Button onClick={handleLoadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
            {/* End-of-list acknowledgment when total is known and all
                agents are loaded — closes the "am I seeing them all?"
                question. Not rendered when total is null (server
                pre-dates includeTotal) or when Load-more is still
                available above. */}
            {!showLoadMore && total !== null && agents.length > 0 && (
              <p className="agent-discover__count agent-discover__count--footer">
                End of results — showing all {total} {total === 1 ? 'agent' : 'agents'}.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AgentDiscover;
